/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { css, OpenCSSProperties, StyleSheet } from 'aphrodite';

import { colors, mixins } from '@blend/consumer-components';

import { PopoverMenu } from '../../NotaryRoom/PopoverMenu/PopoverMenu';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { ResetDocumentConfirmationModal } from '../../NotaryRoom/ResetDocumentConfirmationModal/ResetDocumentConfirmationModal';
import { VoidDocumentConfirmationModal } from '../../NotaryRoom/VoidDocumentConfirmationModal/VoidDocumentConfirmationModal';
import { RemoveEnoteConfirmationModal } from '../../NotaryRoom/RemoveEnoteConfirmationModal/RemoveEnoteConfirmationModal';
import SigningDocument from './SigningDocument';

import { DocumentSigningType, DocumentStatus, ESignProviders } from '@shared/constants/document';
import { isNotaryRoute } from '@utils/routing';
import { CallStatus } from '@shared/constants/call';

export namespace Document {
  export interface Props {
    isSelected: boolean;
    document: SigningDocument;
    handleClick: (documentId: string) => void;
    handleEditClick?: (documentId: string) => void;
    handleFinalizeClick?: (documentId: string) => void;
    styleOverrides?: OpenCSSProperties;
    disabled?: boolean;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: colors.catalyst,
    width: '100%',
    ...mixins.padding(12, 8),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
  },
  selectedWrapper: {
    backgroundColor: colors.reflexLight08,
  },
  buttonWrapper: {
    background: 'none',
    border: 'none',
    textAlign: 'left',
    width: 'calc(100% - 16px)',
    ...mixins.padding(0),
  },
  name: {
    fontSize: 14,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
  },
});

export const Document: React.FC<Document.Props> = ({
  isSelected,
  document,
  handleClick,
  handleEditClick,
  handleFinalizeClick,
  styleOverrides,
  disabled,
}) => {
  const location = useLocation();
  const isNotary = isNotaryRoute(location.pathname);
  const { document: documentState, callDetails } = useRootSelector(state => ({
    document: state.document,
    signingRoom: state.signingRoom,
    callDetails: state.callDetails,
  }));
  const { id, name } = document;

  const [isResetModalOpen, setResetModalOpen] = useState(false);
  const [isVoidModalOpen, setVoidModalOpen] = useState(false);
  const [isEnoteRemovalModalOpen, setIsEnoteRemovalModalOpen] = useState(false);

  const isTaggingActive = document.id === documentState.selectedDoc?.id && !!documentState.editUrl;
  const isOneSpanDoc = document.eSignProvider === ESignProviders.ONESPAN;
  const isVoidedDoc = document.status === DocumentStatus.VOIDED;
  const displayEnoteRemoveButton = document.type === DocumentSigningType.ENOTE;
  const hasCallStarted = callDetails?.status !== CallStatus.OPEN;
  const editDocTagsPopoverItems =
    !isVoidedDoc && (!isTaggingActive || !isOneSpanDoc) && hasCallStarted
      ? [
          {
            name: 'EditDocumentTags',
            display: 'Edit document tags',
            onClick: () => handleEditClick?.(id),
          },
        ]
      : [];
  const finalizePopoverItems =
    !isVoidedDoc && isOneSpanDoc && isTaggingActive
      ? [
          {
            name: 'FinishEditing',
            display: 'Finish editing',
            onClick: () => handleFinalizeClick?.(id),
          },
        ]
      : [];
  const popoverItems = [...editDocTagsPopoverItems, ...finalizePopoverItems];

  const enotePopoverItems = [
    {
      name: 'RemoveEnote',
      display: 'Remove eNote',
      onClick: () => {
        setIsEnoteRemovalModalOpen(true);
      },
    },
  ];

  return (
    <div className={css(styles.wrapper, isSelected && styles.selectedWrapper, styleOverrides)} data-it="Document">
      <button
        className={css(styles.buttonWrapper)}
        onClick={() => handleClick(id)}
        disabled={disabled}
        data-it="Document-button"
      >
        <div className={css(styles.name)}>{name}</div>
      </button>
      {isNotary && document.type !== DocumentSigningType.ENOTE && document.allowDownload && (
        <>
          <PopoverMenu data-it="Popover" iconType="more_horiz" menuItems={popoverItems} />
          <ResetDocumentConfirmationModal
            isModalOpen={isResetModalOpen}
            handleModalClose={() => setResetModalOpen(!isResetModalOpen)}
            document={document}
          />
          <VoidDocumentConfirmationModal
            isModalOpen={isVoidModalOpen}
            handleModalClose={() => setVoidModalOpen(!isVoidModalOpen)}
            document={document}
          />
        </>
      )}
      {isNotary && document.allowDownload && displayEnoteRemoveButton && (
        <>
          <PopoverMenu data-it="Popover" iconType="more_horiz" menuItems={enotePopoverItems} />
          <RemoveEnoteConfirmationModal
            isModalOpen={isEnoteRemovalModalOpen}
            handleModalClose={() => setIsEnoteRemovalModalOpen(!isEnoteRemovalModalOpen)}
            document={document}
          />
        </>
      )}
    </div>
  );
};
